import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest, apiFormRequest, shortDate } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyMissions.css';

/* Missions — F3. One screen, two roles.

   A mission is a thing to go and do: photograph the oldest object on your desk, post about why
   you work here. Everybody sees what is open and hands one answer in; the admin also writes them,
   opens them, and says yes or no to what comes back.

   ★ ONE screen for both, deliberately. The admin is an employee too — they do the missions — and
   a separate management screen would have made the person running the programme the one person
   who cannot take part in it. So the admin's extra powers are extra SECTIONS here, not a
   different page.

   ★ An answer is invisible until it is approved. That is the shape of the game rather than
   caution about content: somebody says the photograph counts. So a pending answer shows to the
   person who handed it in (with the word "waiting" on it) and to the admin's queue, and to
   nobody else — and approving is what puts it on the company feed.

   ★ The catalogue is what makes the screen usable on day one: twenty-eight missions with pictures
   and instructions ship with the platform, and a company writes its own beside them. A screen
   that opened empty with a "New mission" button is a screen nobody fills in. */

const MAX_CAPTION = 500;
/* What a mission answer can be: a photograph we host, or a link to a public post. */
const KINDS = ['picture', 'social'];

const emptyDraft = () => ({ kind: 'picture', mission_id: '', event_id: '',
                            starts_at: '', ends_at: '', description: '', points: '' });
const emptyEntry = () => ({ kind: 'picture', name: '', description: '',
                            photo_url: '', reference_url: '', points: '' });

/* A date input holds a day; the columns hold a moment. The end of a mission is the END of its
   last day, or a mission closing "on Friday" would shut at midnight on Thursday night. */
const startOf = (day) => (day ? `${day}T00:00:00` : '');
const endOf = (day) => (day ? `${day}T23:59:59` : '');

const Avatar = ({ url, name }) => (
  url
    ? <img className="cm-avatar" src={url} alt="" />
    : <span className="cm-avatar cm-avatar--empty">{(name || '?').trim().charAt(0) || '?'}</span>
);

/* What somebody handed in, drawn the same way wherever it appears — in the queue, under the
   mission, on your own row. A picture is the picture; a social mission is a link, and it stays a
   link rather than an embed because the six hosts we accept all refuse to be framed. */
const Answer = ({ submission }) => {
  const { t } = useTranslation();
  if (!submission) return null;
  if (submission.kind === 'picture') {
    return <img className="cm-answer__img" src={submission.answer} alt="" />;
  }
  return (
    <a className="cm-answer__link" href={submission.answer} target="_blank" rel="noreferrer">
      <Icon name="link" className="me-1" />
      {t('missionOpenPost')}
    </a>
  );
};

const CompanyMissions = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  /* open | queue | approved | done | library. Read off the URL so the home can land somebody
     on the right one; empty means "whichever this role opens on" — see `activeTab`. */
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || '');
  const [draft, setDraft] = useState(null);          // opening a mission
  const [entry, setEntry] = useState(null);          // writing a catalogue mission
  const [settings, setSettings] = useState(null);    // the mission being edited (admin)
  const [answering, setAnswering] = useState(null);  // the mission being answered
  const [link, setLink] = useState('');
  const [caption, setCaption] = useState('');

  const load = useCallback(() => {
    apiRequest('GET', '/company/missions')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  /* Every write answers with the whole screen, because almost every write changes two parts of
     it: approving moves a card out of the queue AND changes a count on its mission. */
  const act = (promise) => {
    if (busy) return Promise.resolve();
    setBusy(true);
    setNotice(null);
    return promise
      .then((res) => { setData(res); return res; })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const canManage = !!data.can_manage;
  const missions = data.missions || [];
  const catalogue = data.catalogue || [];
  const pending = data.pending || [];
  const approved = data.approved || [];
  const events = data.events || [];
  const open = missions.filter((m) => m.is_open);
  /* ★ "Open" means two different things to the two roles, and one list served neither. To the
     admin it is the programme: every mission that is running, answered or not. To a member it is
     a TO-DO — a mission they have already uploaded to is not something they still have to do, and
     leaving it there had the same photograph counted under Open and under Finished. */
  const todo = open.filter((m) => !m.mine);
  const mine = missions.filter((m) => m.mine);
  const closed = missions.filter((m) => !m.is_open);
  // What the answered ones have actually paid out — only an approved answer scores.
  const earned = mine.reduce(
    (sum, m) => sum + ((m.mine && m.mine.approved) ? (m.points || 0) : 0), 0);

  const eventName = (id) => (events.find((e) => e.id === id) || {}).name || '';

  const submitPicture = (mission, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    if (caption.trim()) form.append('caption', caption.trim().slice(0, MAX_CAPTION));
    act(apiFormRequest(`/company/missions/${mission.kind}/${mission.id}/submit`, form))
      .then(() => { setAnswering(null); setCaption(''); });
  };

  const submitLink = (mission) => {
    if (!link.trim()) return;
    act(apiRequest('POST', `/company/missions/${mission.kind}/${mission.id}/submit`,
                   { link: link.trim(), caption: caption.trim().slice(0, MAX_CAPTION) }))
      .then(() => { setAnswering(null); setLink(''); setCaption(''); });
  };

  const openMission = () => {
    if (!draft || !draft.mission_id) return;
    act(apiRequest('POST', '/company/missions', {
      kind: draft.kind,
      mission_id: draft.mission_id,
      event_id: draft.event_id || null,
      starts_at: startOf(draft.starts_at),
      ends_at: endOf(draft.ends_at),
      description: draft.description,
      points: draft.points === '' ? null : draft.points,
    })).then((res) => { if (res) setDraft(null); });
  };

  /* The admin's settings panel, opened off one mission. Everything in it is about the MISSION
     — its window, its words, what it is worth — which is exactly what the bare "Close it" link
     was missing: a verb with no subject beside it reads as "close this card". */
  const settingsFor = (mission) => ({
    kind: mission.kind,
    id: mission.id,
    starts_at: (mission.starts_at || '').slice(0, 10),
    ends_at: (mission.ends_at || '').slice(0, 10),
    description: mission.description || '',
    reference_url: mission.reference_url || '',
    points: mission.points === null || mission.points === undefined ? '' : `${mission.points}`,
  });

  const saveSettings = () => {
    if (!settings) return;
    act(apiRequest('PUT', `/company/missions/${settings.kind}/${settings.id}`, {
      starts_at: startOf(settings.starts_at),
      ends_at: endOf(settings.ends_at),
      description: settings.description,
      reference_url: settings.reference_url,
      points: settings.points === '' ? null : settings.points,
    })).then((res) => { if (res) setSettings(null); });
  };

  const addEntry = () => {
    if (!entry || !entry.name.trim()) return;
    act(apiRequest('POST', '/company/missions/catalogue', {
      kind: entry.kind,
      name: entry.name.trim(),
      description: entry.description,
      photo_url: entry.photo_url,
      reference_url: entry.reference_url,
      points: entry.points === '' ? null : entry.points,
    })).then((res) => { if (res) setEntry(null); });
  };

  /* ------------------------------------------------------------------ one mission's card */

  const missionCard = (mission) => {
    const answered = mission.mine;
    const isAnswering = answering && answering.kind === mission.kind
                        && answering.id === mission.id;
    const isEditing = settings && settings.kind === mission.kind && settings.id === mission.id;
    return (
      <Card key={`${mission.kind}-${mission.id}`} className="cm-card">
        <div className="cm-card__head">
          {mission.photo_url && (
            <img className="cm-card__thumb" src={mission.photo_url} alt="" />
          )}
          <div className="cm-card__who">
            <span className="cm-card__name">{mission.name}</span>
            <span className="cm-card__meta">
              {[
                t('missionWorth', { n: mission.points }),
                mission.event_id ? eventName(mission.event_id) : t('missionCompanyWide'),
                mission.ends_at ? t('missionUntil', {
                  date: shortDate(mission.ends_at, i18n.language),
                }) : null,
              ].filter(Boolean).join(' · ')}
            </span>
          </div>
          {!mission.is_open && (
            <span className="cm-pill cm-pill--closed">{t('missionClosedPill')}</span>
          )}
        </div>

        {mission.description && <p className="cm-card__text">{mission.description}</p>}
        {mission.kind === 'social' && mission.reference_url && (
          <a className="cm-answer__link" href={mission.reference_url}
             target="_blank" rel="noreferrer">
            <Icon name="link" className="me-1" />{t('missionExample')}
          </a>
        )}

        {/* The admin's three numbers. Deliberately on the mission and not in the queue: "nine
            handed in, two waiting" is what tells you whether a mission worked. */}
        {canManage && (
          <div className="cm-card__counts">
            <span>{t('missionSubmissions', { n: mission.submissions || 0 })}</span>
            {(mission.pending || 0) > 0 && (
              <button type="button" className="cm-link" onClick={() => setTab('queue')}>
                {t('missionPending', { n: mission.pending })}
              </button>
            )}
          </div>
        )}

        {/* Yours, whatever state it is in. A pending answer says so — somebody who uploads and
            cannot find it has been told their upload failed. */}
        {answered && (
          <div className="cm-mine">
            <Answer submission={answered} />
            <div className="cm-mine__foot">
              <span className={`cm-pill ${answered.approved ? 'cm-pill--live' : 'cm-pill--wait'}`}>
                {answered.approved ? t('missionApprovedPill') : t('missionWaitingPill')}
              </span>
              {/* Only an approved answer scores, so the points appear exactly when they are
                  earned rather than beside every upload as a promise. */}
              {answered.approved && (mission.points || 0) > 0 && (
                <span className="cm-pill cm-pill--points">
                  {t('missionEarned', { n: mission.points })}
                </span>
              )}
              <button
                type="button"
                className="cm-tool"
                disabled={busy}
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (!window.confirm(t('missionWithdrawConfirm'))) return;
                  act(apiRequest('DELETE',
                                 `/company/missions/submission/${answered.kind}/${answered.id}`));
                }}
                aria-label={t('remove')}
              >
                <Icon name="trash" />
              </button>
            </div>
          </div>
        )}

        {/* The way in to doing it. Only while it is open and only while you have not. */}
        {!answered && mission.is_open && !isAnswering && (
          <button
            type="button"
            className="bravos-submit"
            onClick={() => { setAnswering(mission); setLink(''); setCaption(''); }}
          >
            {mission.kind === 'picture' ? t('missionUpload') : t('missionAddLink')}
          </button>
        )}

        {isAnswering && (
          <div className="cm-answering">
            <input
              className="cm-input"
              maxLength={MAX_CAPTION}
              value={caption}
              placeholder={t('missionCaptionPlaceholder')}
              onChange={(e) => setCaption(e.target.value)}
            />
            {mission.kind === 'picture' ? (
              <input
                type="file"
                accept="image/*"
                disabled={busy}
                onChange={(e) => {
                  const file = (e.target.files || [])[0];
                  e.target.value = '';
                  submitPicture(mission, file);
                }}
              />
            ) : (
              <>
                <input
                  className="cm-input"
                  value={link}
                  placeholder={t('missionLinkPlaceholder')}
                  onChange={(e) => setLink(e.target.value)}
                />
                <button
                  type="button"
                  className="bravos-submit"
                  disabled={busy || !link.trim()}
                  onClick={() => submitLink(mission)}
                >
                  {t('send')}
                </button>
              </>
            )}
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={() => setAnswering(null)}>
              {t('cancel')}
            </button>
          </div>
        )}

        {/* Everything that is about the mission rather than about an answer, behind ONE word.
            The card used to carry a bare "Close it" link and nothing else: a verb with no
            subject, which reads as "close this card" as easily as "stop taking answers". */}
        {canManage && (
          <div className="cm-card__tools">
            <button
              type="button"
              className="cm-link"
              onClick={() => setSettings(isEditing ? null : settingsFor(mission))}
            >
              <Icon name="gear" className="me-1" />{t('missionSettings')}
            </button>
          </div>
        )}

        {canManage && isEditing && (
          <div className="cm-form__body">
            <label className="cm-label">
              {t('missionStarts')}
              <input type="date" value={settings.starts_at}
                     onChange={(e) => setSettings({ ...settings, starts_at: e.target.value })} />
            </label>
            <label className="cm-label">
              {t('missionEnds')}
              <input type="date" value={settings.ends_at}
                     onChange={(e) => setSettings({ ...settings, ends_at: e.target.value })} />
            </label>
            <textarea
              className="cm-input"
              rows={3}
              value={settings.description}
              placeholder={t('missionInstructionsOverride')}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
            />
            {mission.kind === 'social' && (
              <input
                className="cm-input"
                value={settings.reference_url}
                placeholder={t('missionExamplePlaceholder')}
                onChange={(e) => setSettings({ ...settings, reference_url: e.target.value })}
              />
            )}
            <input
              type="number"
              min="0"
              max="100"
              className="cm-input"
              value={settings.points}
              placeholder={t('missionPointsPlaceholder')}
              onChange={(e) => setSettings({ ...settings, points: e.target.value })}
            />
            <button type="button" className="bravos-submit" disabled={busy} onClick={saveSettings}>
              {t('save')}
            </button>
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={() => setSettings(null)}>
              {t('cancel')}
            </button>

            {/* The two ways a mission ends, spelled out. Which one is offered depends on
                whether anybody answered: the difference between them is somebody's
                photograph, so a mission with answers can only ever be stopped. */}
            <div className="cm-card__tools">
              {mission.is_open && (
                <button
                  type="button"
                  className="cm-link"
                  disabled={busy}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (!window.confirm(t('missionCloseConfirm'))) return;
                    act(apiRequest('PUT',
                                   `/company/missions/${mission.kind}/${mission.id}`,
                                   { close: true })).then((res) => { if (res) setSettings(null); });
                  }}
                >
                  {t('missionClose')}
                </button>
              )}
              {(mission.submissions || 0) === 0 && (
                <button
                  type="button"
                  className="cm-link cm-link--danger"
                  disabled={busy}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (!window.confirm(t('missionDeleteConfirm'))) return;
                    act(apiRequest('DELETE',
                                   `/company/missions/${mission.kind}/${mission.id}`))
                      .then((res) => { if (res) setSettings(null); });
                  }}
                >
                  {t('missionDelete')}
                </button>
              )}
            </div>
            <span className="cm-card__meta">
              {(mission.submissions || 0) === 0
                ? t('missionSettingsNote') : t('missionSettingsHasAnswers')}
            </span>
          </div>
        )}
      </Card>
    );
  };

  /* ------------------------------------------------------------------ the queue */

  const queueCard = (card) => (
    <Card key={`${card.kind}-${card.id}`} className="cm-card">
      <div className="cm-card__head">
        <Avatar url={card.author_photo} name={card.author} />
        <div className="cm-card__who">
          <span className="cm-card__name">{card.author || t('feedSomeone')}</span>
          <span className="cm-card__meta">
            {[card.mission_name, shortDate(card.uploaded_at, i18n.language)]
              .filter(Boolean).join(' · ')}
          </span>
        </div>
      </div>
      <Answer submission={card} />
      {card.caption && <p className="cm-card__text">{card.caption}</p>}
      <div className="cm-card__tools">
        <button
          type="button"
          className="bravos-submit"
          disabled={busy}
          onClick={() => act(apiRequest(
            'POST', `/company/missions/submission/${card.kind}/${card.id}/review`,
            { approved: true }))}
        >
          <Icon name="check" className="me-1" />{t('missionApprove')}
        </button>
        <button
          type="button"
          className="cm-link cm-link--danger"
          disabled={busy}
          onClick={() => {
            // eslint-disable-next-line no-alert
            if (!window.confirm(t('missionDeclineConfirm'))) return;
            act(apiRequest('POST',
                           `/company/missions/submission/${card.kind}/${card.id}/review`,
                           { approved: false }));
          }}
        >
          {t('missionDecline')}
        </button>
      </div>
    </Card>
  );

  /* ------------------------------------------------------------------ an approved answer */

  /* ★ Approving used to be the last time an admin ever saw a photograph: it left the queue and
     the only place it still existed was the members' feed. This is where the published answers
     live — same card as the queue, one verb instead of two. */
  const approvedCard = (card) => (
    <Card key={`${card.kind}-${card.id}`} className="cm-card">
      <div className="cm-card__head">
        <Avatar url={card.author_photo} name={card.author} />
        <div className="cm-card__who">
          <span className="cm-card__name">{card.author || t('feedSomeone')}</span>
          <span className="cm-card__meta">
            {[card.mission_name, shortDate(card.uploaded_at, i18n.language)]
              .filter(Boolean).join(' · ')}
          </span>
        </div>
        <span className="cm-pill cm-pill--live">{t('missionApprovedPill')}</span>
      </div>
      <Answer submission={card} />
      {card.caption && <p className="cm-card__text">{card.caption}</p>}
      {canManage && (
        <div className="cm-card__tools">
          {/* Not a refusal: the answer is kept and the person is not rung. We changed our mind
              about publishing it, which is our business rather than theirs. */}
          <button
            type="button"
            className="cm-link"
            disabled={busy}
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (!window.confirm(t('missionUnapproveConfirm'))) return;
              act(apiRequest('POST',
                             `/company/missions/submission/${card.kind}/${card.id}/unapprove`));
            }}
          >
            {t('missionUnapprove')}
          </button>
        </div>
      )}
    </Card>
  );

  /* ------------------------------------------------------------------ the library */

  const libraryRow = (m) => (
    <div key={`${m.kind}-${m.id}`} className="cm-lib">
      {m.photo_url && <img className="cm-lib__thumb" src={m.photo_url} alt="" />}
      <div className="cm-lib__body">
        <span className="cm-lib__name">
          {m.name}
          {!m.owner_company_id && <span className="cm-lib__badge">{t('missionShared')}</span>}
        </span>
        {m.description && <span className="cm-lib__text">{m.description}</span>}
      </div>
      <div className="cm-lib__acts">
        <button
          type="button"
          className="cm-link"
          onClick={() => { setDraft({ ...emptyDraft(), kind: m.kind, mission_id: m.id }); }}
        >
          {t('missionOpen')}
        </button>
        {m.owner_company_id && (
          <button
            type="button"
            className="cm-link cm-link--danger"
            disabled={busy}
            onClick={() => act(apiRequest('DELETE',
                                          `/company/missions/catalogue/${m.kind}/${m.id}`))}
          >
            {t('remove')}
          </button>
        )}
      </div>
    </div>
  );

  /* ★ Order is role-driven, and the FIRST tab is the one that opens. The admin runs a
     programme, so they land on what is running; a member comes back to see whether their
     photograph made it, so they land on Finished. */
  const tabs = canManage
    ? [['open', open.length], ['queue', pending.length], ['approved', approved.length],
       ['done', closed.length], ['library', catalogue.length]]
    : [['done', mine.length], ['open', todo.length]];
  const activeTab = tabs.some(([key]) => key === tab) ? tab : tabs[0][0];

  return (
    <Base activeNav="home">
      {/* ★ Header written the way every other Bravos screen writes it — Back, then a Title with
          CHILDREN. It used to pass `title`/`subtitle` props, which Title does not take: the page
          rendered an empty <h3>, so it had no name on it at all and the heading's collapsed
          margins let the tab row ride up under the back arrow. */}
      <Back to="/member-home" />
      <Title my="1" className="mt-3">
        <Icon name="flag" theme="primary" className="me-2" />
        {t('missionsTitle')}
      </Title>
      <p className="bravos-q__hint text-center mb-3">
        {canManage ? t('missionsAdminNote') : t('missionsMemberNote')}
      </p>

      {notice && <Card className="cm-notice">{notice}</Card>}

      <div className="cm-tabs">
        {tabs.map(([key, n]) => (
          <button
            key={key}
            type="button"
            className={`cm-tab ${activeTab === key ? 'is-on' : ''}`}
            onClick={() => setTab(key)}
          >
            {t(`missionsTab_${key}`)}
            {n > 0 && <span className="cm-tab__n">{n}</span>}
          </button>
        ))}
      </div>

      {/* ---------------------------------------------------------- opening a mission */}
      {canManage && activeTab === 'library' && (
        <Card className="cm-form">
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            onClick={() => setEntry(entry ? null : emptyEntry())}
          >
            <Icon name="plus" className="me-1" />{t('missionNew')}
          </button>
          {entry && (
            <div className="cm-form__body">
              <select value={entry.kind}
                      onChange={(e) => setEntry({ ...entry, kind: e.target.value })}>
                {KINDS.map((k) => <option key={k} value={k}>{t(`missionKind_${k}`)}</option>)}
              </select>
              <input
                className="cm-input"
                maxLength={200}
                value={entry.name}
                placeholder={t('missionNamePlaceholder')}
                onChange={(e) => setEntry({ ...entry, name: e.target.value })}
              />
              <textarea
                className="cm-input"
                rows={3}
                value={entry.description}
                placeholder={t('missionInstructionsPlaceholder')}
                onChange={(e) => setEntry({ ...entry, description: e.target.value })}
              />
              <input
                className="cm-input"
                value={entry.photo_url}
                placeholder={t('missionImagePlaceholder')}
                onChange={(e) => setEntry({ ...entry, photo_url: e.target.value })}
              />
              {entry.kind === 'social' && (
                <input
                  className="cm-input"
                  value={entry.reference_url}
                  placeholder={t('missionExamplePlaceholder')}
                  onChange={(e) => setEntry({ ...entry, reference_url: e.target.value })}
                />
              )}
              <input
                type="number"
                min="0"
                max="100"
                className="cm-input"
                value={entry.points}
                placeholder={t('missionPointsPlaceholder')}
                onChange={(e) => setEntry({ ...entry, points: e.target.value })}
              />
              <button type="button" className="bravos-submit" disabled={busy || !entry.name.trim()}
                      onClick={addEntry}>
                {t('save')}
              </button>
            </div>
          )}
        </Card>
      )}

      {draft && canManage && (
        <Card className="cm-form">
          <span className="cm-form__title">
            {(catalogue.find((c) => c.id === draft.mission_id) || {}).name || t('missionOpen')}
          </span>
          <div className="cm-form__body">
            {/* Company-wide is the DEFAULT and the empty option, not a choice called "none":
                a mission belongs to the company unless somebody ties it to an evening. */}
            <select value={draft.event_id}
                    onChange={(e) => setDraft({ ...draft, event_id: e.target.value })}>
              <option value="">{t('missionCompanyWide')}</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}{e.date ? ` · ${shortDate(e.date, i18n.language)}` : ''}
                </option>
              ))}
            </select>
            <label className="cm-label">
              {t('missionStarts')}
              <input type="date" value={draft.starts_at}
                     onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })} />
            </label>
            <label className="cm-label">
              {t('missionEnds')}
              <input type="date" value={draft.ends_at}
                     onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} />
            </label>
            <textarea
              className="cm-input"
              rows={3}
              value={draft.description}
              placeholder={t('missionInstructionsOverride')}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
            <input
              type="number"
              min="0"
              max="100"
              className="cm-input"
              value={draft.points}
              placeholder={t('missionPointsPlaceholder')}
              onChange={(e) => setDraft({ ...draft, points: e.target.value })}
            />
            <button type="button" className="bravos-submit" disabled={busy} onClick={openMission}>
              {t('missionOpen')}
            </button>
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={() => setDraft(null)}>
              {t('cancel')}
            </button>
          </div>
        </Card>
      )}

      {/* ---------------------------------------------------------- the lists */}
      {activeTab === 'open' && ((canManage ? open : todo).length > 0
        ? (canManage ? open : todo).map(missionCard)
        : (
          <Card>
            <p className="text-muted mb-0">
              {canManage || open.length === 0
                ? t('missionsNoneOpen') : t('missionsAllUploaded')}
            </p>
          </Card>
        ))}

      {activeTab === 'queue' && canManage && (pending.length > 0
        ? pending.map(queueCard)
        : <Card><p className="text-muted mb-0">{t('missionsQueueEmpty')}</p></Card>)}

      {/* Approved ANSWERS, not missions — the published photographs, newest first. */}
      {activeTab === 'approved' && canManage && (approved.length > 0
        ? approved.map(approvedCard)
        : <Card><p className="text-muted mb-0">{t('missionsNoneApproved')}</p></Card>)}

      {/* "Finished" is the missions that have stopped — for a member, the ones they answered,
          with what those answers actually paid out on top. A waiting answer is here too: it is
          handed in, and the only other place it could live is a to-do list it does not belong on. */}
      {activeTab === 'done' && !canManage && earned > 0 && (
        <Card className="cm-earned">{t('missionsEarnedTotal', { n: earned })}</Card>
      )}
      {activeTab === 'done' && ((canManage ? closed : mine).length > 0
        ? (canManage ? closed : mine).map(missionCard)
        : <Card><p className="text-muted mb-0">{t('missionsNoneDone')}</p></Card>)}

      {activeTab === 'library' && canManage && (catalogue.length > 0
        ? <Card className="cm-library">{catalogue.map(libraryRow)}</Card>
        : <Card><p className="text-muted mb-0">{t('missionsLibraryEmpty')}</p></Card>)}
    </Base>
  );
};

export default CompanyMissions;
